import {
  getInputValue,
  getSelectedOptionIds,
  isWidgetPending,
  useExperienceAnswers,
} from '@cascade/react/experiences';
import type { TAnswer } from '@cascade/react/experiences';
import { useI18n } from '@cascade/react/i18n';
import { Card, cardVariants, Markdown, Text } from '@cascade/ui-library';
import classes from './ExperienceWidget.module.scss';
import type { TExperienceWidgetProps } from './ExperienceWidget.types.ts';
import { InputWidget } from './input-widget/index.ts';
import { SelectWidget } from './select-widget/index.ts';
import { ExperienceHeading } from '#views/experience/experience-step/experience-heading/index.ts';
import { HelpButton } from '#views/experience/experience-step/experience-section/help-button/index.ts';
import { SideStepButton } from '#views/experience/experience-step/experience-section/side-step-button/index.ts';

export const ExperienceWidget = ({
  widget,
  failureMessage,
}: TExperienceWidgetProps) => {
  const variant = cardVariants.find(
    (cardVariant) => cardVariant === widget.semantic_color,
  );

  const showFailure = Boolean(failureMessage) && isWidgetPending(widget);

  const { translate } = useI18n();
  const { answers, canAnswer, setAnswer } = useExperienceAnswers();

  const label = widget.title || translate('experience.widget.answer');
  const answer = answers[widget.id];
  const inputValue = getInputValue(answer);
  const selectedOptionIds = getSelectedOptionIds(answer);

  const changeAnswer = (value: TAnswer) => setAnswer(widget.id, value);

  return (
    <Card
      className={classes['experience-widget']}
      variant={variant}
      fillVariant={Boolean(variant)}
      padding='lg'
      borderRadius='md'
    >
      <div className={classes.content}>
        <ExperienceHeading
          icon={widget.icon}
          iconColor={widget.icon_color}
          title={widget.title}
          description={widget.description}
          level='h4'
        />
        {widget.content ? (
          <Markdown content={widget.content} tableStyle='striped' />
        ) : null}
        {showFailure ? (
          <Text type='body-2' color='text-danger' content={failureMessage} />
        ) : null}
        {widget.widget_type === 'input' ? (
          <InputWidget
            widget={widget}
            label={label}
            value={inputValue}
            isDisabled={!canAnswer}
            onChange={changeAnswer}
          />
        ) : null}
        {widget.widget_type === 'select' ? (
          <SelectWidget
            widget={widget}
            label={label}
            value={selectedOptionIds}
            isDisabled={!canAnswer}
            onChange={changeAnswer}
          />
        ) : null}
        {widget.side_step_button ? (
          <div className={classes['side-step']}>
            <SideStepButton button={widget.side_step_button} />
          </div>
        ) : null}
      </div>
      {widget.help_prompt ? <HelpButton prompt={widget.help_prompt} /> : null}
    </Card>
  );
};
